import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AutoGrowTextarea } from '@/components/common/AutoGrowTextarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Receipt, Plus, Search, MoreHorizontal, Pencil, Trash2, Copy, Tag, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['all', 'service', 'parts', 'labor', 'pump', 'repair', 'general'];
const CATEGORY_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  parts: 'bg-emerald-100 text-emerald-700',
  labor: 'bg-purple-100 text-purple-700',
  pump: 'bg-cyan-100 text-cyan-700',
  repair: 'bg-amber-100 text-amber-700',
  general: 'bg-slate-100 text-slate-700'
};

export default function InvoiceTemplatesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ 
    name: '', 
    description: '', 
    category: 'general', 
    notes: '', 
    due_days: 30,
    is_active: true, 
    items: [{ sku: '', name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }] 
  });

  const [lineItemSearch, setLineItemSearch] = useState('');
  const [lineItemResults, setLineItemResults] = useState([]);
  const [activeItemIdx, setActiveItemIdx] = useState(null);
  const searchTimeoutRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const params = { active_only: false };
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      const res = await api.get('/invoice-templates', { params });
      let filtered = res.data;
      if (search) {
        const s = search.toLowerCase();
        filtered = filtered.filter(tp => tp.name?.toLowerCase().includes(s) || tp.description?.toLowerCase().includes(s));
      }
      setTemplates(filtered);
    } catch { toast.error(t('invoiceTemplates.errorLoading')); }
    finally { setLoading(false); }
  }, [categoryFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ 
      name: '', 
      description: '', 
      category: 'general', 
      notes: '', 
      due_days: 30,
      is_active: true, 
      items: [{ sku: '', name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }] 
    });
    setLineItemSearch(''); setLineItemResults([]); setActiveItemIdx(null);
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({
      name: template.name || '', 
      description: template.description || '', 
      category: template.category || 'general',
      notes: template.notes || '', 
      due_days: template.due_days || 30,
      is_active: template.is_active !== false,
      items: template.items?.length ? template.items.map(i => ({ 
        sku: i.sku || '', 
        name: i.name || '', 
        description: i.description || '', 
        quantity: i.quantity || 1, 
        unit: i.unit || 'unit', 
        unit_price: i.unit_price || 0 
      })) : [{ sku: '', name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }]
    });
    setLineItemSearch(''); setLineItemResults([]); setActiveItemIdx(null);
    setDialogOpen(true);
  };

  const duplicateTemplate = (template) => {
    setEditing(null);
    setForm({
      name: `${template.name} (${t('common.copy')})`, 
      description: template.description || '', 
      category: template.category || 'general',
      notes: template.notes || '', 
      due_days: template.due_days || 30,
      is_active: true,
      items: template.items?.length ? template.items.map(i => ({ 
        sku: i.sku || '', 
        name: i.name || '', 
        description: i.description || '', 
        quantity: i.quantity || 1, 
        unit: i.unit || 'unit', 
        unit_price: i.unit_price || 0 
      })) : [{ sku: '', name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }]
    });
    setDialogOpen(true);
    toast.info(t('invoiceTemplates.duplicating'));
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { sku: '', name: '', description: '', quantity: 1, unit: 'unit', unit_price: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(val) || 0 : val };
    setForm({ ...form, items });
  };
  const calcTotal = () => form.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0);

  const searchLineItems = async (query, idx) => {
    setActiveItemIdx(idx);
    setLineItemSearch(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query || query.length < 1) { setLineItemResults([]); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      try { const res = await api.get('/products/search', { params: { q: query } }); setLineItemResults(res.data); }
      catch { setLineItemResults([]); }
    }, 150);
  };

  const selectLineItem = (lineItem, idx) => {
    const items = [...form.items];
    items[idx] = { 
      ...items[idx], 
      sku: lineItem.sku, 
      name: lineItem.name, 
      description: lineItem.description || lineItem.name, 
      quantity: lineItem.default_quantity || 1, 
      unit: lineItem.unit || 'unit', 
      unit_price: lineItem.unit_price || 0 
    };
    setForm({ ...form, items });
    setLineItemResults([]); setActiveItemIdx(null); setLineItemSearch('');
    toast.success(`"${lineItem.name}" ${t('common.added').toLowerCase()}`);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('invoiceTemplates.nameRequired')); return; }
    if (form.items.length === 0 || !form.items[0].name) { toast.error(t('invoiceTemplates.addItemFirst')); return; }
    try {
      let saved;
      if (editing) { 
        const res = await api.put(`/invoice-templates/${editing.id}`, form); 
        saved = res.data;
        toast.success(t('invoiceTemplates.updated')); 
      } else { 
        const res = await api.post('/invoice-templates', form); 
        saved = res.data;
        toast.success(t('invoiceTemplates.created')); 
      }
      // Save keeps the dialog open (Alian: "no quiero estar abriendo y
      // cerrando... quiero dejar la plantilla abierta"). Switch a brand-new
      // template into edit mode so a second Save does PUT, not another POST.
      setEditing(saved);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('invoiceTemplates.errorSaving')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/invoice-templates/${id}`); toast.success(t('invoiceTemplates.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const applyTemplate = (template) => { 
    navigate('/invoices', { state: { useTemplate: template } }); 
  };

  return (
    <div data-testid="invoice-templates-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('invoiceTemplates.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('invoiceTemplates.totalTemplates', { count: templates.length })}</p>
        </div>
        <Button data-testid="new-invoice-template-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> {t('invoiceTemplates.newTemplate')}
        </Button>
      </div>

      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="p-4">
          <p className="text-sm text-emerald-700">{t('invoiceTemplates.infoBanner')}</p>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-invoice-templates" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search')} className="pl-10 h-9 bg-white" />
        </div>
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="w-auto">
          <TabsList className="bg-white border border-gray-200 h-9">
            {CATEGORIES.map(c => (
              <TabsTrigger key={c} value={c} data-testid={`invoice-template-category-${c}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                {t(`invoiceTemplates.categories.${c}`, { defaultValue: c })}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse" />)}
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-white border-gray-100">
          <CardContent className="py-16 text-center">
            <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">{t('invoiceTemplates.noTemplates')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('invoiceTemplates.createFirstTemplate')}</p>
            <Button onClick={openCreate} className="mt-4 bg-[#003366] hover:bg-[#004080] text-white">
              <Plus className="w-4 h-4 mr-2" /> {t('invoiceTemplates.createTemplate')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template, idx) => (
            <Card key={template.id} data-testid={`invoice-template-card-${template.id}`} className={`bg-white border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${!template.is_active ? 'opacity-60' : ''}`} style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${CATEGORY_COLORS[template.category] || 'bg-slate-100 text-slate-600'}`}>
                        {t(`invoiceTemplates.categories.${template.category}`, { defaultValue: template.category })}
                      </span>
                      {!template.is_active && <span className="text-xs px-2 py-0.5 rounded-md bg-gray-200 text-gray-500">{t('invoiceTemplates.inactive')}</span>}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 truncate">{template.name}</h3>
                    {template.description && <p className="text-sm text-gray-500 line-clamp-2 mt-1">{template.description}</p>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => applyTemplate(template)}><Receipt className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('invoiceTemplates.useTemplate')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(template)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => duplicateTemplate(template)}><Copy className="w-3.5 h-3.5 mr-2" />{t('common.duplicate')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(template.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* Due Days Badge */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    <Calendar className="w-3 h-3" />
                    <span>{t('invoiceTemplates.dueDays', { days: template.due_days || 30 })}</span>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">{t('invoiceTemplates.itemsIncluded', { count: template.items?.length || 0 })}</p>
                  <div className="space-y-1">
                    {template.items?.slice(0, 3).map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 truncate">{item.name || item.description}</span>
                        <span className="text-gray-400 ml-2">${(item.unit_price || 0).toFixed(0)}</span>
                      </div>
                    ))}
                    {template.items?.length > 3 && <p className="text-xs text-gray-400">+{template.items.length - 3} {t('common.more')}...</p>}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-400">{t('invoiceTemplates.baseTotal')}</span>
                    <span className="text-lg font-bold text-gray-900 ml-2">${(template.subtotal || 0).toLocaleString()}</span>
                  </div>
                  <Button size="sm" onClick={() => applyTemplate(template)} className="bg-[#003366] hover:bg-[#004080] text-white">
                    <Receipt className="w-3.5 h-3.5 mr-1" /> {t('common.use')}
                  </Button>
                </div>
                {template.usage_count > 0 && <p className="text-[10px] text-gray-400 mt-2">{t('invoiceTemplates.usageCount', { count: template.usage_count })}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#3399FF]" />
              {editing ? t('invoiceTemplates.editTemplate') : t('invoiceTemplates.newTemplate')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('invoiceTemplates.templateName')} *</Label>
                <Input data-testid="invoice-template-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('invoiceTemplates.templateNamePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceTemplates.category')}</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c !== 'all').map(c => <SelectItem key={c} value={c}>{t(`invoiceTemplates.categories.${c}`, { defaultValue: c })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('invoiceTemplates.dueDaysLabel')}</Label>
                <Select value={String(form.due_days)} onValueChange={v => setForm({ ...form, due_days: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 {t('common.days')}</SelectItem>
                    <SelectItem value="30">30 {t('common.days')}</SelectItem>
                    <SelectItem value="45">45 {t('common.days')}</SelectItem>
                    <SelectItem value="60">60 {t('common.days')}</SelectItem>
                    <SelectItem value="90">90 {t('common.days')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                <Switch id="is_active" checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                <Label htmlFor="is_active" className="text-sm cursor-pointer">{t('invoiceTemplates.activeLabel')}</Label>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('common.description')}</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">{t('invoiceTemplates.items')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-3 h-3 mr-1" /> {t('invoiceTemplates.addItem')}
                </Button>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50 border border-emerald-100">
                <Tag className="w-4 h-4 text-emerald-500" />
                <p className="text-xs text-emerald-600">{t('invoiceTemplates.skuSearchHint')}</p>
              </div>
              <div className="space-y-3">
                {form.items.map((item, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-gray-50 border border-gray-100 space-y-2">
                    <div className="flex gap-2 items-start relative">
                      <div className="relative w-32">
                        <Input
                          value={item.sku || ''}
                          onChange={e => { updateItem(idx, 'sku', e.target.value); searchLineItems(e.target.value, idx); }}
                          onFocus={() => { setActiveItemIdx(idx); if (item.sku) searchLineItems(item.sku, idx); }}
                          placeholder="SKU..."
                          className="bg-white h-9 font-mono text-sm"
                        />
                        {activeItemIdx === idx && lineItemResults.length > 0 && (
                          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-auto w-80">
                            {lineItemResults.map((li) => (
                              <button key={li.id} type="button" onClick={() => selectLineItem(li, idx)} className="w-full px-3 py-2.5 text-left hover:bg-emerald-50 border-b last:border-0 transition-colors">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm text-[#003366] font-bold">{li.sku}</span>
                                    {li.supplier_sku && <span className="text-[10px] text-gray-400 font-mono">({li.supplier_sku})</span>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-emerald-600">${li.unit_price?.toFixed(2)}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{li.source === 'inventory' ? 'Stock' : t('lineItems.title')}</span>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-800 truncate mt-0.5">{li.name}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Input value={item.name || ''} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder={t('lineItems.name')} className="flex-1 bg-white h-9 font-medium" />
                      {form.items.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-9 w-9 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                    <AutoGrowTextarea data-testid={`tpl-item-desc-${idx}`} value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder={t('common.description')} className="bg-white text-sm" />
                    <div className="flex gap-2 items-center">
                      <Input type="number" value={item.quantity || ''} onChange={e => updateItem(idx, 'quantity', e.target.value)} onFocus={e => e.target.select()} className="w-20 bg-white h-9" min="0" />
                      <Input value={item.unit || 'unit'} onChange={e => updateItem(idx, 'unit', e.target.value)} className="w-16 bg-white h-9 text-xs" />
                      <span className="text-gray-400">x</span>
                      <span className="text-gray-500 text-sm">$</span>
                      <Input type="number" value={item.unit_price || ''} onChange={e => updateItem(idx, 'unit_price', e.target.value)} onFocus={e => e.target.select()} className="w-24 bg-white h-9" min="0" step="0.01" />
                      <span className="text-gray-400">=</span>
                      <span className="text-sm font-semibold text-gray-700 min-w-[80px] text-right">${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end px-3">
                <div className="text-right">
                  <span className="text-sm text-gray-500">{t('invoiceTemplates.baseTotal')} </span>
                  <span className="text-lg font-bold text-gray-900">${calcTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('common.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder={t('invoiceTemplates.notesPlaceholder')} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-invoice-template-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('invoiceTemplates.saveChanges') : t('invoiceTemplates.createTemplate')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
